import React, { useState } from 'react';
import { Eye, EyeOff, Loader2, Sparkles } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useRegisterForm from '../../hooks/useRegisterForm';
import SiteLogo from '../../components/SiteLogo';

/**
 * Register Variant 2 — Dark Glass
 * Full dark background, subtle grid, centered glassmorphism card, ambient mouse-glow.
 */
const RegisterDarkGlass = () => {
  const { t } = useTranslation('auth');
  const {
    username, email, password,
    showPassword, loading, error, validationErrors,
    showCookieNotice,
    setShowPassword,
    handleUsernameChange,
    handleEmailChange, handlePasswordChange,
    handleSubmit,
    handleAcceptAll, handleOnlyNecessary,
    socialProviders, handleSocialLogin,
  } = useRegisterForm();

  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const loginUrl = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center relative overflow-x-hidden overflow-y-auto py-10" onMouseMove={handleMouseMove}>
      {/* Ambient mouse glow */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-30" style={{
        background: `radial-gradient(600px circle at ${mousePos.x}px ${mousePos.y}px, rgba(16,185,129,0.12), transparent 40%)`
      }} />

      {/* Grid background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }} />

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-500/8 rounded-full blur-[120px]" />
      <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-violet-500/8 rounded-full blur-[120px]" />

      {/* Logo */}
      <div className="absolute top-8 start-8 z-20">
        <SiteLogo forceDark className="h-7 opacity-60" />
      </div>

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-lg mx-4">
        {/* Glow behind card */}
        <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 rounded-3xl blur-xl opacity-60 pointer-events-none" />

        <div className="relative bg-white/[0.05] backdrop-blur-2xl border border-white/[0.08] rounded-3xl p-10 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400 font-medium mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              {t('register.createAccountBadge')}
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-2">{t('register.joinCommunity')}</h1>
            <p className="text-gray-500 text-sm">{t('register.joinSubtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t('register.usernameLabel')}</label>
              <input
                type="text"
                value={username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                placeholder={t('register.usernamePlaceholder')}
                className={`w-full px-4 py-3 bg-white/[0.04] border rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none transition-all ${
                  validationErrors.username
                    ? 'border-red-500/50 focus:border-red-500'
                    : 'border-white/[0.08] focus:border-emerald-500/50 focus:bg-white/[0.06]'
                }`}
                required
              />
              {validationErrors.username && <p className="text-red-400 text-xs mt-1.5">{validationErrors.username}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t('register.emailLabel')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder={t('register.emailPlaceholder')}
                className={`w-full px-4 py-3 bg-white/[0.04] border rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none transition-all ${
                  validationErrors.email
                    ? 'border-red-500/50 focus:border-red-500'
                    : 'border-white/[0.08] focus:border-emerald-500/50 focus:bg-white/[0.06]'
                }`}
                required
              />
              {validationErrors.email && <p className="text-red-400 text-xs mt-1.5">{validationErrors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t('register.passwordLabel')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  className={`w-full px-4 py-3 pe-12 bg-white/[0.04] border rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none transition-all ${
                    validationErrors.password
                      ? 'border-red-500/50 focus:border-red-500'
                      : 'border-white/[0.08] focus:border-emerald-500/50 focus:bg-white/[0.06]'
                  }`}
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {validationErrors.password && <p className="text-red-400 text-xs mt-1.5">{validationErrors.password}</p>}
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">{error}</div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full py-3.5 rounded-xl font-bold text-sm text-white overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 group-hover:from-emerald-400 group-hover:to-teal-500 transition-all" />
              <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
              <span className="relative flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('register.creatingAccount')}</> : t('register.signUpButton')}
              </span>
            </button>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/[0.06]" /></div>
              <div className="relative flex justify-center"><span className="bg-[#0a0a0f] px-4 text-xs font-medium text-gray-600 uppercase">{t('register.or')}</span></div>
            </div>

            {/* Social Buttons */}
            {socialProviders.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {socialProviders.includes('google') && (
                  <button type="button" onClick={() => handleSocialLogin('google')} className="group flex items-center justify-center py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:bg-white/[0.08] hover:border-white/10 transition-all">
                    <svg className="w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  </button>
                )}
                {socialProviders.includes('twitter') && (
                  <button type="button" onClick={() => handleSocialLogin('twitter')} className="group flex items-center justify-center py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:bg-white/[0.08] hover:border-white/10 transition-all">
                    <svg className="w-5 h-5 text-white opacity-60 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </button>
                )}
              </div>
            )}

            {/* Login link */}
            <p className="text-center text-sm text-gray-500 mt-4">
              {t('register.alreadyHaveAccount')}{' '}
              <Link to={loginUrl} className="text-emerald-400 hover:text-emerald-300 font-semibold">{t('register.logInLink')}</Link>
            </p>
          </form>
        </div>

        {/* Terms below card */}
        <p className="text-center text-[11px] text-gray-600 mt-6 px-4">
          {t('register.termsAgreement')}{' '}
          <a href="/page/terms-of-service" className="underline hover:text-gray-400">{t('register.termsOfService')}</a> {t('common:label.and')}{' '}
          <a href="/page/privacy-policy" className="underline hover:text-gray-400">{t('register.privacyPolicy')}</a>, {t('register.ageConfirm')}
        </p>
      </div>

      {/* Cookie Notice */}
      {showCookieNotice && (
        <div className="fixed bottom-0 start-0 end-0 z-50">
          <div className="bg-[#12121a] border-t border-white/[0.06]">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-500">
                {t('register.cookieMessage')}{' '}
                <a href="/page/cookie-policy" className="text-emerald-400 font-medium hover:underline">{t('register.cookieLearnMore')}</a>
              </p>
              <div className="flex gap-3">
                <button onClick={handleOnlyNecessary} className="px-4 py-2 text-sm font-medium text-gray-400 bg-white/[0.05] border border-white/[0.08] hover:bg-white/[0.1] rounded-full transition-colors">{t('register.necessaryOnly')}</button>
                <button onClick={handleAcceptAll} className="px-5 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-400 rounded-full transition-colors">{t('register.acceptAll')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterDarkGlass;
